import React from 'react';
import { FieldValues } from 'react-hook-form';
import {
    usStatesAndTerritories,
    canadianProvincesAndTerritories,
} from '../../../constants/states';
import FormProvider from '../../primitives/form-provider';
import Input from '../../primitives/input';
import Checkbox from '../../primitives/checkbox';
import Select, { SelectOption } from '../../primitives/select';
import FormAlert from '../../primitives/form-alert';
import RowSet from '../../primitives/grid/row-set';
import Col from '../../primitives/grid/col';
import Row from '../../primitives/grid/row';
import FormSuccess from '../../primitives/form-success';
import StyledButton from '../../primitives/styled-button';

const optInCopy = 'By clicking on the submit button, you agree that Servpro Franchisor, LLC or '
    + 'Servpro Industries (Canada) ULC may call, email and/or text you for additional '
    + 'information related to your inquiry, provide information regarding this franchise '
    + 'opportunity and answer questions. We will only use your information for the purposes '
    + 'of determining if you are a suitable candidate to become an independently owned and '
    + 'operated franchise of Servpro Franchisor, LLC or Servpro Industries (Canada) ULC. '
    + 'At no time will we use this information for any other purposes or provide it to '
    + 'third parties. Data and messaging rates may apply.';

const REGIONS = ['United States', 'Canada'];

const HOW_DID_HEAR_OPTIONS = [
    'Advertisement',
    'BizBuySell',
    'Brokers',
    'Entrepreneur',
    'Franchise Times',
    'Import',
    'Internet',
    'Internet – Servpro.com',
    'Radio',
    'Referred By',
    'Servpro Franchise Owner',
    'Servpro HQ Employee',
    'Social Media',
    'Tradeshow',
    'TV',
    'Used Servpro Services',
    'I work for a Servpro Franchise',
];

interface FranchiseOwnershipFormProps {
    submitButtonText?: string
    successMessageText?: string
}

const FranchiseOwnershipForm = ({ submitButtonText, successMessageText }: FranchiseOwnershipFormProps): JSX.Element => {
    const [formSuccess, setSuccess] = React.useState<boolean | undefined>(undefined);
    const [loading, setIsLoading] = React.useState(false);

    const [region, setRegion] = React.useState<string | undefined>();

    const isUsRegionSelected = region === REGIONS[0];
    const getCurrentStates = (region: string): string[] => (
        region === REGIONS[0]
            ? usStatesAndTerritories.map(state => state.name)
            : canadianProvincesAndTerritories.map(province => province.name)
    );

    const onSubmit = (e: FieldValues): void => {
        setIsLoading(true);
        fetch('/api/forms/franconnect', {
            method: 'POST',
            body: JSON.stringify(e),
            headers: {
                'Content-Type': 'application/json',
                'cache-control': 'no-cache',
            },
        }).then((res) => {
            setIsLoading(false);
            return res.json();
        }).then(() => {
            setSuccess(true);
            return true;
        }).catch(() => {
            setIsLoading(false);
            setSuccess(false);
            return false;
        });
    };

    return (
        <>
            {!formSuccess && (
                <Row>
                    <Col breakpoints={{ dt: { span: 7 } }}>
                        <FormProvider disabled={loading} onSubmit={onSubmit}>
                            <RowSet breakpoints={{
                                dt: { between: 12 },
                            }}
                            >
                                <Select
                                    name="country"
                                    placeholder="Choose your region..."
                                    onChange={(e) => {
                                        setRegion(e);
                                    }}
                                    required
                                >
                                    {REGIONS.map(option => (
                                        <SelectOption key={option} value={option}>
                                            {option}
                                        </SelectOption>
                                    ))}
                                </Select>
                                {region && (
                                    <>
                                        <Select
                                            name="state"
                                            placeholder={`${isUsRegionSelected ? 'State' : 'Province'} of interest...`}
                                            required
                                        >
                                            {getCurrentStates(region).map(option => (
                                                <SelectOption key={option} value={option}>
                                                    {option}
                                                </SelectOption>
                                            ))}
                                        </Select>
                                        <Input name="first-name" placeholder="First Name" required maxLength={50} />
                                        <Input name="last-name" placeholder="Last Name" required maxLength={50} />
                                        <Input name="phone" type="tel" placeholder="Phone Number" required />
                                        <Input name="email" type="email" placeholder="Email Address" required />
                                        <Input name="address" placeholder="Address" required minLength={6} />
                                        <Input name="city" placeholder="City" required minLength={2} />
                                        <Select
                                            name="lead-source-details"
                                            placeholder="How did you hear about us?"
                                            required
                                        >
                                            {HOW_DID_HEAR_OPTIONS.map(option => (
                                                <SelectOption key={option} value={option}>
                                                    {option}
                                                </SelectOption>
                                            ))}
                                        </Select>
                                        <Checkbox
                                            LabelFontSize='sm'
                                            size='sm'
                                            name='opt-in'
                                            required
                                        >
                                            {optInCopy}
                                        </Checkbox>
                                        <div css={{ paddingTop: '20px' }}>
                                            <StyledButton type="submit" loading={loading}>
                                                {submitButtonText || 'Submit'}
                                            </StyledButton>
                                        </div>
                                    </>
                                )}
                            </RowSet>
                        </FormProvider>
                    </Col>
                </Row>
            )}
            {formSuccess && (
                <FormSuccess dangerouslySetInnerHTML={{ __html: successMessageText || 'Thank you! We will contact you soon.' }} />
            )}
            {formSuccess === false && (
                <FormAlert>
                    There was an issue submitting your enquiry.
                </FormAlert>
            )}
        </>
    );
};
export default FranchiseOwnershipForm;
